<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9000] flex items-center justify-center p-4"
        @click.self="close"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]" @click="close"></div>

        <!-- Modal card -->
        <div ref="modalEl" class="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
             role="dialog" aria-modal="true" aria-label="Toegevoegd aan je winkelwagen">

          <!-- Groene header met vinkje -->
          <div class="bg-[#22c55e] px-6 py-4 flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <i class="bi bi-check-lg text-white text-[1.1rem]"></i>
            </div>
            <p class="text-white font-semibold text-[0.95rem]">Toegevoegd aan je winkelwagen!</p>
            <button
              @click="close"
              class="ml-auto text-white/70 hover:text-white transition-colors"
              aria-label="Sluiten"
            >
              <i class="bi bi-x-lg text-[1.1rem]"></i>
            </button>
          </div>

          <!-- Product info -->
          <div class="px-6 py-5 flex gap-4 items-center border-b border-gray-100">
            <div class="w-20 h-20 rounded-xl bg-page overflow-hidden shrink-0 flex items-center justify-center">
              <img
                v-if="productImage"
                :src="productImage"
                :alt="productName"
                class="w-full h-full object-contain p-1"
              />
              <i v-else class="bi bi-box-seam text-[#bbb] text-[2rem]"></i>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-ink text-[0.9rem] leading-snug line-clamp-2">{{ productName }}</p>
              <p class="text-ink-5 text-[0.82rem] mt-0.5">Aantal: {{ lastAdded?.quantity }}</p>
              <p class="text-ink font-bold text-[0.95rem] mt-1">{{ formattedPrice }}</p>
            </div>
          </div>

          <!-- Cart summary -->
          <div class="px-6 py-3 bg-[#f9fafb] flex items-center justify-between">
            <span class="text-[0.82rem] text-ink-4">
              <i class="bi bi-cart3 mr-1.5 text-brand"></i>
              {{ cartStore.itemCount }} {{ cartStore.itemCount === 1 ? 'artikel' : 'artikelen' }} in je winkelwagen
            </span>
            <span class="font-bold text-[0.9rem] text-ink">{{ formattedCartTotal }}</span>
          </div>

          <!-- Buttons -->
          <div class="px-6 py-5 flex flex-col gap-2.5">
            <router-link
              to="/checkout"
              @click="close"
              class="w-full bg-[#111] hover:bg-[#222] text-white font-bold text-[0.9rem] py-3 rounded-xl text-center no-underline flex items-center justify-center gap-2 modal-btn-checkout"
            >
              <i class="bi bi-lock-fill text-[0.8rem]"></i>
              Direct afrekenen
            </router-link>
            <div class="flex gap-2.5">
              <button
                @click="close"
                class="flex-1 border border-gray-200 text-ink-4 hover:bg-gray-50 font-semibold text-[0.82rem] py-2.5 rounded-xl modal-btn-secondary"
              >
                Verder winkelen
              </button>
              <router-link
                to="/cart"
                @click="close"
                class="flex-1 bg-brand hover:bg-brand-hover text-ink font-bold text-[0.82rem] py-2.5 rounded-xl text-center no-underline flex items-center justify-center gap-1.5 modal-btn-primary"
              >
                <i class="bi bi-cart-check"></i>
                Winkelwagen
              </router-link>
            </div>
          </div>

          <!-- Upsell: gratis verzending progress -->
          <div v-if="showShippingProgress" class="px-6 pb-5">
            <div class="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl px-4 py-3">
              <div class="flex items-center justify-between mb-1.5">
                <span class="text-[0.78rem] text-[#166534] font-semibold">
                  <i class="bi bi-truck mr-1"></i>
                  {{ shippingProgressText }}
                </span>
                <span class="text-[0.78rem] text-[#166534] font-semibold">{{ shippingProgressPercent }}%</span>
              </div>
              <div class="h-1.5 bg-[#dcfce7] rounded-full overflow-hidden">
                <div
                  class="h-full bg-[#22c55e] rounded-full" style="transition: width 480ms cubic-bezier(0.23,1,0.32,1)"
                  :style="{ width: shippingProgressPercent + '%' }"
                ></div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useCartModal } from '@composables/useCartModal'
import { useCartStore } from '@stores/cart'
import { useEscToClose } from '@composables/useEscToClose'
import { useFocusTrap } from '@composables/useFocusTrap'
import { formatPrice } from '@/utils/price'

const { isOpen, lastAdded, close } = useCartModal()
const cartStore = useCartStore()
const modalEl = ref(null)
useEscToClose(isOpen, close)
useFocusTrap(isOpen, modalEl)

const FREE_SHIPPING_THRESHOLD = 50  // euro

const productName  = computed(() => lastAdded.value?.product?.name || '')
const productImage = computed(() => lastAdded.value?.product?.images?.[0]?.src || null)

const formattedPrice = computed(() => {
  const qty   = lastAdded.value?.quantity || 1
  const price = parseFloat(lastAdded.value?.product?.sale_price || lastAdded.value?.product?.price || 0)
  return formatPrice(price * qty)
})

const formattedCartTotal = computed(() => formatPrice(cartStore.total || 0))

const cartSubtotal = computed(() => parseFloat(cartStore.subtotal || 0))

const showShippingProgress = computed(() => cartSubtotal.value < FREE_SHIPPING_THRESHOLD)

const shippingProgressPercent = computed(() =>
  Math.min(100, Math.round((cartSubtotal.value / FREE_SHIPPING_THRESHOLD) * 100))
)

const shippingProgressText = computed(() => {
  return cartSubtotal.value >= FREE_SHIPPING_THRESHOLD
    ? 'Gratis verzending!'
    : `Nog ${formatPrice(FREE_SHIPPING_THRESHOLD - cartSubtotal.value)} voor gratis verzending`
})
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity var(--dur-base) var(--ease-out);
}
.modal-fade-enter-active .relative {
  transition: transform var(--dur-slow) var(--ease-in-out), opacity var(--dur-base) var(--ease-out);
}
.modal-fade-leave-active .relative {
  transition: transform var(--dur-base) var(--ease-out), opacity var(--dur-fast) var(--ease-out);
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .relative {
  transform: scale(0.95) translateY(8px);
  opacity: 0;
}

/* Button motion — scoped here to avoid Tailwind class conflicts */
.modal-btn-primary {
  transition: background var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
}
.modal-btn-primary:hover { transform: translateY(-1px); }
.modal-btn-primary:active { transform: scale(0.98) translateY(0); }

.modal-btn-secondary {
  transition: background var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
}
.modal-btn-secondary:active { transform: scale(0.98); }

.modal-btn-checkout {
  transition: background var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
}
.modal-btn-checkout:hover { transform: translateY(-1px); }
.modal-btn-checkout:active { transform: scale(0.98); }
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
